import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import authService from '@/services/authService';
import loyaltyService from '@/services/loyaltyService';
import { getFieldsForArena } from '@/services/fieldApiService';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import {
  LoyaltyProgram,
  LoyaltyActiveSystem,
  LoyaltySummaryResponse,
  LoyaltyParticipantsResponse,
  LoyaltyParticipantItem,
} from '@/types/loyalty';

import LoyaltyRuleSummaryCard from './LoyaltyRuleSummaryCard';
import LoyaltySystemSelector from './LoyaltySystemSelector';
import PointsSettingsForm from './forms/PointsSettingsForm';
import MembershipSettingsForm from './forms/MembershipSettingsForm';
import StampSettingsForm from './forms/StampSettingsForm';
import SummaryTab from './tabs/SummaryTab';
import ParticipantsTab from './tabs/ParticipantsTab';
import AddMemberModal from './modals/AddMemberModal';
import ManualAdjustModal from './modals/ManualAdjustModal';
import SwitchSystemConfirmModal from './modals/SwitchSystemConfirmModal';

export const LoyaltyPage: React.FC = () => {
  const arenaSlug = authService.getSlug() || localStorage.getItem('arenaSlug') || '';
  const userRole = authService.getRole();
  const isStaff = userRole === 'staff';

  // Navigation tab when program is already saved as active
  const [activeTab, setActiveTab] = useState<'summary' | 'participants' | 'settings'>('summary');

  // Loading states
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [saving, setSaving] = useState(false);

  // Program State
  const [program, setProgram] = useState<LoyaltyProgram | null>(null);
  const [initialData, setInitialData] = useState<LoyaltyProgram | null>(null);
  const [arenaFields, setArenaFields] = useState<{ id: number; name: string }[]>([]);

  // Validation Banner Error
  const [validationError, setValidationError] = useState<string | null>(null);

  // Data for Active Program Tabs
  const [summaryData, setSummaryData] = useState<LoyaltySummaryResponse | null>(null);
  const [participantsData, setParticipantsData] = useState<LoyaltyParticipantsResponse | null>(null);
  const [participantsSearch, setParticipantsSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [participantsPage, setParticipantsPage] = useState(1);

  // Modals state
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<LoyaltyParticipantItem | null>(null);
  const [pendingSystemSwitch, setPendingSystemSwitch] = useState<LoyaltyActiveSystem | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(participantsSearch);
      setParticipantsPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [participantsSearch]);

  // Load Program & Fields
  const fetchProgram = useCallback(async () => {
    if (!arenaSlug) return;
    try {
      setLoadingProgram(true);
      const data = await loyaltyService.getProgram(arenaSlug);
      setProgram(data);
      setInitialData(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal memuat program loyalty'));
    } finally {
      setLoadingProgram(false);
    }
  }, [arenaSlug]);

  const fetchArenaFields = useCallback(async () => {
    if (!arenaSlug) return;
    try {
      const res = await getFieldsForArena(arenaSlug, 1, 100);
      if (res && res.data && res.data.fields) {
        setArenaFields(res.data.fields.map((f: any) => ({ id: f.id, name: f.name })));
      }
    } catch {
      // Ignore field error
    }
  }, [arenaSlug]);

  const isSavedActive = Boolean(initialData?.is_active);

  const fetchSummary = useCallback(async () => {
    if (!arenaSlug || !isSavedActive) return;
    try {
      setLoadingSummary(true);
      const res = await loyaltyService.getSummary(arenaSlug);
      setSummaryData(res);
    } catch {
      // Ignore summary fetch error
    } finally {
      setLoadingSummary(false);
    }
  }, [arenaSlug, isSavedActive]);

  const fetchParticipants = useCallback(async () => {
    if (!arenaSlug || !isSavedActive) return;
    try {
      setLoadingParticipants(true);
      const res = await loyaltyService.getParticipants(arenaSlug, {
        page: participantsPage,
        limit: 10,
        search: debouncedSearch.trim(),
      });
      setParticipantsData(res);
    } catch {
      // Ignore error
    } finally {
      setLoadingParticipants(false);
    }
  }, [arenaSlug, isSavedActive, participantsPage, debouncedSearch]);

  useEffect(() => {
    fetchProgram();
    fetchArenaFields();
  }, [fetchProgram, fetchArenaFields]);

  // Hydrate default empty stamp_applicable_field_ids from DB with all arena field IDs
  useEffect(() => {
    if (!program || arenaFields.length === 0) return;
    if (!program.stamp_applicable_field_ids || program.stamp_applicable_field_ids.length === 0) {
      const allFieldIds = arenaFields.map((f) => f.id);
      setProgram((prev) =>
        prev && (!prev.stamp_applicable_field_ids || prev.stamp_applicable_field_ids.length === 0)
          ? { ...prev, stamp_applicable_field_ids: allFieldIds }
          : prev
      );
      setInitialData((prev) =>
        prev && (!prev.stamp_applicable_field_ids || prev.stamp_applicable_field_ids.length === 0)
          ? { ...prev, stamp_applicable_field_ids: allFieldIds }
          : prev
      );
    }
  }, [program, arenaFields]);

  useEffect(() => {
    if (isSavedActive) {
      if (activeTab === 'summary') {
        fetchSummary();
      } else if (activeTab === 'participants') {
        fetchParticipants();
      }
    }
  }, [isSavedActive, activeTab, fetchSummary, fetchParticipants]);

  // Dirty check: has anything changed?
  const isDirty = useMemo(() => {
    if (!program || !initialData) return false;
    return JSON.stringify(program) !== JSON.stringify(initialData);
  }, [program, initialData]);

  // System change handler
  const handleSystemChange = (newSystem: LoyaltyActiveSystem) => {
    if (!program) return;
    if (program.active_system === newSystem) return;

    // If program is saved as active and currently running, prompt warning modal (LY-03)
    if (program.is_active && initialData?.is_active) {
      setPendingSystemSwitch(newSystem);
    } else {
      setProgram({ ...program, active_system: newSystem });
      setValidationError(null);
    }
  };

  const confirmSystemSwitch = () => {
    if (pendingSystemSwitch && program) {
      setProgram({ ...program, active_system: pendingSystemSwitch });
      setPendingSystemSwitch(null);
      setValidationError(null);
    }
  };

  // Form patch helper
  const handleFormPatch = (patch: Partial<LoyaltyProgram>) => {
    if (!program) return;
    setProgram({ ...program, ...patch });
    setValidationError(null);
  };

  // Reset/Cancel changes
  const handleCancel = () => {
    if (initialData) {
      setProgram(JSON.parse(JSON.stringify(initialData)));
      setValidationError(null);
      showToast.info('Perubahan dibatalkan');
    }
  };

  // Validate form before save (aligned with backend loyalty_service.go)
  const validate = (): string | null => {
    if (!program) return 'Data program tidak ditemukan';

    if (program.active_system === 'points') {
      if (!program.points_spend_per_point || program.points_spend_per_point < 1000) {
        return 'Belanja per 1 poin minimal Rp 1.000.';
      }
      if (!program.points_redemption_rate || program.points_redemption_rate < 1) {
        return 'Nilai 1 poin saat ditukar minimal Rp 1.';
      }
      if (!program.points_sources || program.points_sources.length === 0) {
        return 'Pilih minimal satu sumber poin.';
      }
      if (!program.points_min_redeem || program.points_min_redeem < 1) {
        return 'Minimum tukar minimal 1 poin.';
      }
      if (
        program.points_has_expiry &&
        (!program.points_expiry_months ||
          program.points_expiry_months < 1 ||
          program.points_expiry_months > 36)
      ) {
        return 'Masa berlaku poin antara 1 hingga 36 bulan.';
      }
    } else if (program.active_system === 'membership') {
      if (program.membership_registration_fee < 0) {
        return 'Biaya pendaftaran tidak boleh negatif.';
      }
      if (
        !program.membership_duration_months ||
        program.membership_duration_months < 1 ||
        program.membership_duration_months > 24
      ) {
        return 'Masa aktif membership antara 1 hingga 24 bulan.';
      }
      if (
        program.membership_booking_discount < 0 ||
        program.membership_booking_discount > 100
      ) {
        return 'Diskon booking harus antara 0% hingga 100%.';
      }
      if (
        program.membership_product_discount < 0 ||
        program.membership_product_discount > 100
      ) {
        return 'Diskon sewa & produk harus antara 0% hingga 100%.';
      }
      if (
        program.membership_priority_booking &&
        (!program.membership_priority_days ||
          program.membership_priority_days < 1 ||
          program.membership_priority_days > 30)
      ) {
        return 'Prioritas buka jadwal antara 1 hingga 30 hari.';
      }
      const hasBenefit =
        program.membership_booking_discount > 0 ||
        program.membership_product_discount > 0 ||
        (program.membership_priority_booking && program.membership_priority_days > 0);
      if (!hasBenefit) {
        return 'Minimal satu benefit membership harus lebih dari 0 (diskon atau prioritas).';
      }
    } else if (program.active_system === 'stamp') {
      if (
        !program.stamp_target_count ||
        program.stamp_target_count < 2 ||
        program.stamp_target_count > 50
      ) {
        return 'Jumlah booking target antara 2 hingga 50 kali.';
      }
      if (
        !program.stamp_reward_free_hours ||
        program.stamp_reward_free_hours < 1 ||
        program.stamp_reward_free_hours > 3
      ) {
        return 'Hadiah gratis antara 1 hingga 3 jam.';
      }
      if (!program.stamp_min_booking_duration || program.stamp_min_booking_duration < 1) {
        return 'Minimum durasi booking minimal 1 jam.';
      }
      if (
        !program.stamp_applicable_field_ids ||
        program.stamp_applicable_field_ids.length === 0
      ) {
        return 'Pilih minimal satu lapangan yang berlaku.';
      }
      if (
        program.stamp_has_expiry &&
        (!program.stamp_expiry_months ||
          program.stamp_expiry_months < 1 ||
          program.stamp_expiry_months > 24)
      ) {
        return 'Batas waktu kumpul antara 1 hingga 24 bulan.';
      }
    }

    return null;
  };

  // Save changes
  const handleSave = async () => {
    const errorMsg = validate();
    if (errorMsg) {
      setValidationError(errorMsg);
      showToast.error(errorMsg);
      return;
    }

    try {
      setSaving(true);
      setValidationError(null);
      const updated = await loyaltyService.updateProgram(arenaSlug, program!);
      setProgram(updated);
      setInitialData(JSON.parse(JSON.stringify(updated)));
      showToast.success('Konfigurasi program loyalty berhasil disimpan');

      if (updated.is_active) {
        fetchSummary();
        fetchParticipants();
      }
    } catch (err) {
      const errStr = getApiErrorMessage(err, 'Gagal menyimpan perubahan');
      setValidationError(errStr);
      showToast.error(errStr);
    } finally {
      setSaving(false);
    }
  };

  if (loadingProgram) {
    return (
      <div className="py-24 text-center">
        <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin mx-auto mb-2" />
        <h4 className="text-sm font-semibold text-[#0E1726]">Memuat Loyalty...</h4>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="rounded-2xl border border-[#E0E6ED] bg-white p-12 text-center max-w-[420px] mx-auto my-12">
        <Icon icon="ph:warning-circle" className="w-10 h-10 text-[#E7515A] mx-auto mb-2" />
        <h3 className="text-base font-semibold text-[#0E1726]">
          Program Loyalty Tidak Ditemukan
        </h3>
        <p className="text-xs text-[#8C8C8C] mt-1">
          Tidak dapat memuat pengaturan loyalty untuk arena ini.
        </p>
      </div>
    );
  }

  const showSettingsView = !isSavedActive || activeTab === 'settings';

  const ruleSectionLabel: Record<LoyaltyActiveSystem, string> = {
    points: 'ATURAN POIN',
    membership: 'ATURAN MEMBERSHIP',
    stamp: 'ATURAN BOOKING GRATIS',
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-8xl mx-auto pb-12">
      {/* Top Page Header (No Card Wrapper) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Loyalty</h1>

        {showSettingsView && !isStaff && (
          <div className="flex items-center gap-2.5 self-end sm:self-auto">
            {isDirty && (
              <button
                type="button"
                disabled={saving}
                onClick={handleCancel}
                className="rounded-full bg-[#FFF5EE] hover:bg-[#FFE8DB] text-[#FC480D] px-5 py-2 text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>
            )}
            <button
              type="button"
              disabled={!isDirty || saving}
              onClick={handleSave}
              className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white px-5 py-2 text-sm font-semibold shadow-xs inline-flex items-center justify-center gap-1.5 transition-colors cursor-pointer disabled:bg-[#FC480D]/50 disabled:cursor-not-allowed"
            >
              {saving && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
              <span>Simpan</span>
            </button>
          </div>
        )}
      </div>

      {/* Pill Tabs when program is saved as active */}
      {isSavedActive && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('summary')}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'summary'
                ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                : 'border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50'
            }`}
          >
            <Icon icon="ph:chart-bar" className="w-3.5 h-3.5" />
            <span>Ringkasan</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('participants')}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'participants'
                ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                : 'border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50'
            }`}
          >
            <Icon icon="ph:users" className="w-3.5 h-3.5" />
            <span>Peserta</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'settings'
                ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                : 'border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50'
            }`}
          >
            <Icon icon="ph:gear-six" className="w-3.5 h-3.5" />
            <span>Pengaturan</span>
          </button>
        </div>
      )}

      {/* Validation Error Alert */}
      {validationError && showSettingsView && (
        <div className="rounded-xl bg-[#E7515A]/10 border border-[#E7515A] px-4 py-3 flex items-start gap-2.5 text-xs sm:text-[13px] text-[#E7515A]">
          <Icon icon="ph:warning-circle" className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Konfigurasi Belum Valid</p>
            <p className="mt-0.5">{validationError}</p>
          </div>
        </div>
      )}

      {/* Active Program Views */}
      {isSavedActive && activeTab === 'summary' && (
        <SummaryTab data={summaryData} loading={loadingSummary} onRefresh={fetchSummary} />
      )}

      {isSavedActive && activeTab === 'participants' && (
        <ParticipantsTab
          activeSystem={program.active_system}
          data={participantsData}
          loading={loadingParticipants}
          searchQuery={participantsSearch}
          onSearchChange={setParticipantsSearch}
          page={participantsPage}
          onPageChange={setParticipantsPage}
          onOpenAddMember={() => setIsAddMemberOpen(true)}
          onOpenAdjust={(p) => setAdjustTarget(p)}
          isStaff={isStaff}
        />
      )}

      {/* Settings View: Full-width System Selector + 2-Column Rules & Summary */}
      {showSettingsView && (
        <div className="space-y-5">
          {/* Full-Width System Selector Card */}
          <LoyaltySystemSelector
            value={program.active_system}
            onChange={handleSystemChange}
            isActive={program.is_active}
            onToggleActive={(active) => handleFormPatch({ is_active: active })}
            disabled={isStaff || saving}
          />

          {/* Bottom 2-Column Layout: Rules Form (8 cols) + Realtime Summary (4 cols) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            <div className="lg:col-span-8 rounded-2xl border border-[#E0E6ED] bg-white p-5 sm:p-6 shadow-xs">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[#8C8C8C] mb-5">
                {ruleSectionLabel[program.active_system]}
              </h3>

              {program.active_system === 'points' && (
                <PointsSettingsForm
                  formData={program}
                  onChange={handleFormPatch}
                  disabled={isStaff || saving}
                />
              )}

              {program.active_system === 'membership' && (
                <MembershipSettingsForm
                  formData={program}
                  onChange={handleFormPatch}
                  disabled={isStaff || saving}
                />
              )}

              {program.active_system === 'stamp' && (
                <StampSettingsForm
                  formData={program}
                  onChange={handleFormPatch}
                  arenaFields={arenaFields}
                  disabled={isStaff || saving}
                />
              )}
            </div>

            <div className="lg:col-span-4 sticky top-6">
              <LoyaltyRuleSummaryCard formData={program} arenaFields={arenaFields} />
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddMemberModal
        isOpen={isAddMemberOpen}
        onClose={() => setIsAddMemberOpen(false)}
        arenaSlug={arenaSlug}
        program={program}
        onSuccess={() => {
          fetchSummary();
          fetchParticipants();
        }}
      />

      <ManualAdjustModal
        isOpen={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        arenaSlug={arenaSlug}
        system={program.active_system}
        participant={adjustTarget}
        onSuccess={() => {
          fetchSummary();
          fetchParticipants();
        }}
      />

      <SwitchSystemConfirmModal
        isOpen={!!pendingSystemSwitch}
        onClose={() => setPendingSystemSwitch(null)}
        onConfirm={confirmSystemSwitch}
        currentSystem={program.active_system}
        targetSystem={pendingSystemSwitch || 'points'}
      />
    </div>
  );
};

export default LoyaltyPage;
